import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChannelPaymentStatus, ChannelPaymentSubStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import {
  columnFilter,
  DateRangeFilterFields,
  enumOptions,
  RangeFilterFields,
  SelectFilterField,
} from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { CHANNEL_PAYMENT_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { formatStatusLabel } from "@/lib/utils";
import { listChannelPayments } from "@/api/channels";
import { listMerchants } from "@/api/merchants";
import { getInvoiceAssetOptions } from "@/api/invoices";
import { type ChannelPaymentListItem, type ListChannelPaymentsParams } from "@/api/types";

// Task 14 (feature-031 Phase B): platform-wide Channel Payments list — same
// list-page shape as _app.channels.tsx / _app.invoices.tsx. `channelId`
// (per Task 8's backend filter set) is deliberately not exposed as its own
// UI filter here — the channel detail page's own Payments sub-table already
// covers that case by passing it directly, and there's no role-safe options
// source to back a channel-picker dropdown on this page.
type ChannelPaymentFilters = {
  status?: ChannelPaymentStatus;
  subStatus?: ChannelPaymentSubStatus;
  merchantId?: string;
  assetId?: string;
  amountUsdMin?: string;
  amountUsdMax?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: ChannelPaymentFilters = {};

function validateChannelPaymentsSearch(search: Record<string, unknown>): TableSearch<ChannelPaymentFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as ChannelPaymentStatus) : undefined,
    subStatus: typeof search.subStatus === "string" ? (search.subStatus as ChannelPaymentSubStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    amountUsdMin: typeof search.amountUsdMin === "string" ? search.amountUsdMin : undefined,
    amountUsdMax: typeof search.amountUsdMax === "string" ? search.amountUsdMax : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/channel-payments")({
  validateSearch: validateChannelPaymentsSearch,
  component: ChannelPaymentsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function RowLink({ channelPaymentId, children }: { channelPaymentId: string; children: ReactNode }) {
  return (
    <Link to="/channel-payments/$channelPaymentId" params={{ channelPaymentId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

function ChannelPaymentsPage() {
  const tableState = useTableState<ChannelPaymentFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListChannelPaymentsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListChannelPaymentsParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel-payments", params],
    queryFn: () => listChannelPayments(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const { data: assetOptions } = useQuery({
    queryKey: ["invoice-asset-options"],
    queryFn: getInvoiceAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<ChannelPaymentListItem>[] = [
    {
      key: "publicId",
      header: "Payment ID",
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <span className="font-mono group-hover:underline">{p.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      cell: (p) => (
        <MerchantCell merchantId={p.merchant.id} businessName={p.merchant.businessName} publicId={p.merchant.publicId} />
      ),
    },
    {
      key: "channel",
      header: "Channel",
      cell: (p) => (
        <Link to="/channels/$channelId" params={{ channelId: p.channel.id }} className="group -m-2 block p-2">
          <span className="block group-hover:underline">{p.channel.label}</span>
          <span className="block text-xs text-muted-foreground">{p.channel.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status", "subStatus"], (draft, set) => (
        <>
          <SelectFilterField
            id="filter-status"
            label="Status"
            allLabel="All statuses"
            options={enumOptions(Object.values(ChannelPaymentStatus))}
            value={draft.status}
            onChange={(v) => set("status", v as ChannelPaymentStatus | undefined)}
          />
          <SelectFilterField
            id="filter-subStatus"
            label="Sub-status"
            allLabel="All sub-statuses"
            options={enumOptions(Object.values(ChannelPaymentSubStatus))}
            value={draft.subStatus}
            onChange={(v) => set("subStatus", v as ChannelPaymentSubStatus | undefined)}
          />
        </>
      )),
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <StatusBadge label={formatStatusLabel(p.subStatus ?? p.status)} color={CHANNEL_PAYMENT_STATUS_COLOR[p.status]} />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      filter: columnFilter(tableState, ["assetId"], (draft, set) => (
        <SelectFilterField
          id="filter-asset"
          label="Asset"
          allLabel="All assets"
          options={(assetOptions ?? []).map((a) => ({
            value: a.id,
            label: `${a.symbol} · ${formatStatusLabel(a.network)}`,
          }))}
          value={draft.assetId}
          onChange={(v) => set("assetId", v)}
        />
      )),
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <AssetLabel symbol={p.asset.symbol} network={p.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "cryptoAmountUsd",
      header: "Amount",
      filter: columnFilter(tableState, ["amountUsdMin", "amountUsdMax"], (draft, set) => (
        <RangeFilterFields
          idPrefix="filter-amount"
          minLabel="Min (USD)"
          maxLabel="Max (USD)"
          min={draft.amountUsdMin}
          max={draft.amountUsdMax}
          onMinChange={(v) => set("amountUsdMin", v)}
          onMaxChange={(v) => set("amountUsdMax", v)}
        />
      )),
      sortable: true,
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <Amount native={p.cryptoAmount} symbol={p.asset.symbol} usd={p.cryptoAmountUsd} />
        </RowLink>
      ),
    },
    {
      key: "fee",
      header: "Fee",
      cell: (p) => (
        <RowLink channelPaymentId={p.id}>
          <Amount native={p.cryptoFee} symbol={p.asset.symbol} usd={p.feeUsd} />
        </RowLink>
      ),
    },
    {
      key: "senderAddress",
      header: "Sender",
      cell: (p) => <HashLink value={p.senderAddress} type="address" network={p.asset.network} className="text-xs" />,
    },
    {
      key: "createdAt",
      header: "Created",
      filter: columnFilter(tableState, ["from", "to"], (draft, set) => (
        <DateRangeFilterFields
          idPrefix="filter-date"
          from={draft.from}
          to={draft.to}
          onFromChange={(v) => set("from", v)}
          onToChange={(v) => set("to", v)}
        />
      )),
      sortable: true,
      cell: (p) => <RowLink channelPaymentId={p.id}>{formatDate(p.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load channel payments." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(p) => p.id}
        emptyMessage="No channel payments match your filters."
      />
    </div>
  );
}
